import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/common/Button';
import { Sparkles, Check, Loader2, GraduationCap, Briefcase, ArrowRight, Layers, Zap, Scale, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, StripePlan } from '@/services/stripeService';
import { toast } from 'sonner';

interface UpgradePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Si se indica, fija el tab inicial */
  defaultTab?: 'opositor' | 'pro';
  /** Si true, oculta el selector de tabs y bloquea el tipo de plan al defaultTab */
  lockTab?: boolean;
}

type PlanType = 'opositor' | 'pro';
type OpositorTier = 'basic' | 'base_comun' | 'single' | 'multi';
type Frequency = 'monthly' | 'yearly';

const OPOSITOR_TIERS = {
  basic: {
    title: 'Básico',
    subtitle: 'Práctica una oposición',
    icon: Zap,
    features: [
      'Tests tipo test ilimitados en la oposición elegida',
      'Flashcards de repaso',
      'Chat tutor IA para dudas puntuales del temario',
      'Métricas de progreso y racha',
    ],
    monthly: { price: '6,99€', period: '/mes', stripePlan: 'oposiciones_basic_monthly' as StripePlan },
    yearly:  { price: '49€', period: '/año', savings: 'Ahorra 35€', stripePlan: 'oposiciones_basic_yearly' as StripePlan },
  },
  base_comun: {
    title: 'Base Común Pro',
    subtitle: 'Especial legislación 42 temas',
    icon: Scale,
    features: [
      'Temario completo de 42 temas generales',
      'Tests ilimitados + Supuestos Prácticos de leyes',
      'Flashcards de artículos y plazos clave',
      'Generador IA de infografías y resúmenes PDF',
    ],
    monthly: { price: '9,99€', period: '/mes', stripePlan: 'base_comun_monthly' as StripePlan },
    yearly:  { price: '79€', period: '/año', savings: 'Ahorra 41€', stripePlan: 'base_comun_yearly' as StripePlan },
  },
  single: {
    title: '1 Oposición Pro',
    subtitle: 'Preparación integral 1 cuerpo',
    icon: Shield,
    features: [
      'Temario oficial completo y actualizado',
      'Simulacros oficiales cronometrados',
      'Casos y supuestos prácticos con IA',
      'Generador ilimitado de esquemas e infografías',
    ],
    monthly: { price: '14,99€', period: '/mes', stripePlan: 'oposiciones_single_monthly' as StripePlan },
    yearly:  { price: '119€', period: '/año', savings: 'Ahorra 60€', stripePlan: 'oposiciones_single_yearly' as StripePlan },
  },
  multi: {
    title: 'All-Access',
    subtitle: 'Acceso total e ilimitado',
    icon: Sparkles,
    features: [
      'Acceso a TODO el catálogo de oposiciones',
      'Base Común + C1, C2, IIPP, GC, PN, Secretaría',
      'Generador IA ilimitado de materiales y mapas',
      'Cambio libre entre cuerpos sin restricciones',
    ],
    monthly: { price: '19,99€', period: '/mes', stripePlan: 'oposiciones_multi_monthly' as StripePlan },
    yearly:  { price: '159€', period: '/año', savings: 'Ahorra 80€', stripePlan: 'oposiciones_multi_yearly' as StripePlan },
  },
} as const;

const PRO_CONFIG = {
  icon: Briefcase,
  title: 'Munilex Pro',
  subtitle: 'El copiloto del Funcionario Público',
  features: [
    'Redacción inteligente de informes y resoluciones',
    '+90 modelos municipales profesionales',
    'Análisis avanzado de documentos PDF/Word',
    'Exportación directa a Word',
    'Gestor de plazos con recordatorios',
    'Normativa local, autonómica y estatal',
  ],
  monthly: { price: '120€', period: '/mes + IVA', stripePlan: 'pro_monthly' as StripePlan },
  yearly:  { price: '100€', period: '/mes + IVA', totalPrice: '1.200€/año', savings: 'Ahorra 240€', stripePlan: 'pro_yearly' as StripePlan },
};

export function UpgradePlanModal({ open, onOpenChange, defaultTab = 'opositor', lockTab = false }: UpgradePlanModalProps) {
  const navigate = useNavigate();
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>(defaultTab);
  const [opositorTier, setOpositorTier] = useState<OpositorTier>('basic');
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<StripePlan | null>(null);

  const getStripePlan = (): StripePlan => {
    if (selectedPlanType === 'opositor') {
      return OPOSITOR_TIERS[opositorTier][frequency].stripePlan;
    }
    return frequency === 'yearly' ? PRO_CONFIG.yearly.stripePlan : PRO_CONFIG.monthly.stripePlan;
  };

  const handleSubscribe = async () => {
    const plan = getStripePlan();
    try {
      setLoadingPlan(plan);
      const checkoutUrl = await createCheckoutSession(plan);
      window.open(checkoutUrl, '_blank');
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error instanceof Error ? error.message : 'Error al iniciar el proceso de pago');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleViewAllPlans = () => {
    onOpenChange(false);
    navigate(selectedPlanType === 'opositor' ? '/pricing/opositores' : '/pricing/funcionarios');
  };

  const isOpositor = selectedPlanType === 'opositor';
  const headerTitle = isOpositor ? 'Munilex Academy' : PRO_CONFIG.title;
  const headerSubtitle = isOpositor
    ? 'Tu preparador 24/7 para superar tu oposición'
    : PRO_CONFIG.subtitle;
  const HeaderIcon = isOpositor ? GraduationCap : PRO_CONFIG.icon;

  const features = isOpositor
    ? OPOSITOR_TIERS[opositorTier].features
    : PRO_CONFIG.features;

  const buttonText = isOpositor
    ? `Continuar con ${OPOSITOR_TIERS[opositorTier].title} ${frequency === 'yearly' ? 'Anual' : 'Mensual'}`
    : `Continuar con Pro ${frequency === 'yearly' ? 'Anual' : 'Mensual'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-0 max-h-[90vh] overflow-y-auto">
        {/* Header con gradiente */}
        <div
          className="px-6 py-6 text-center text-white"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #065f46 100%)' }}
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm mx-auto mb-2">
            <HeaderIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <h2 className="text-xl font-bold">{headerTitle}</h2>
          </div>
          <p className="text-white/80 text-xs">{headerSubtitle}</p>
        </div>

        <div className="px-6 py-5">
          {/* Tabs (ocultos si lockTab) */}
          {!lockTab && (
            <div className="flex rounded-lg bg-gray-100 p-1 mb-5">
              <button
                onClick={() => setSelectedPlanType('opositor')}
                className="flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all"
                style={{
                  background: selectedPlanType === 'opositor' ? 'white' : 'transparent',
                  color: selectedPlanType === 'opositor' ? '#1e1b4b' : '#6b7280',
                  boxShadow: selectedPlanType === 'opositor' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                }}
              >
                <GraduationCap className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                Opositores
              </button>
              <button
                onClick={() => setSelectedPlanType('pro')}
                className="flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all"
                style={{
                  background: selectedPlanType === 'pro' ? 'white' : 'transparent',
                  color: selectedPlanType === 'pro' ? '#1e1b4b' : '#6b7280',
                  boxShadow: selectedPlanType === 'pro' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                }}
              >
                <Briefcase className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                Funcionarios
              </button>
            </div>
          )}

          {/* Selector de tier (solo opositor) */}
          {isOpositor && (
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {(Object.keys(OPOSITOR_TIERS) as OpositorTier[]).map((tier) => {
                const isSel = opositorTier === tier;
                const TierIcon = OPOSITOR_TIERS[tier].icon;
                return (
                  <button
                    key={tier}
                    onClick={() => setOpositorTier(tier)}
                    className="relative p-2.5 rounded-xl border-2 text-left transition-all"
                    style={{
                      borderColor: isSel ? '#059669' : '#e5e7eb',
                      background: isSel ? 'rgba(5,150,105,0.06)' : 'white',
                    }}
                  >
                    {tier === 'basic' && (
                      <div
                        className="absolute -top-2 right-2 px-2 py-0.2 rounded-full text-[9px] font-bold text-white bg-emerald-600"
                      >
                        Más Popular
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <TierIcon className="h-3.5 w-3.5 text-emerald-700" />
                      <span className="text-xs font-bold text-gray-900">
                        {OPOSITOR_TIERS[tier].title}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 leading-tight">
                      {OPOSITOR_TIERS[tier].subtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Frecuencia */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              onClick={() => setFrequency('monthly')}
              className="relative p-3 rounded-xl border-2 text-left transition-all"
              style={{
                borderColor: frequency === 'monthly' ? '#059669' : '#e5e7eb',
                background: frequency === 'monthly' ? 'rgba(5,150,105,0.06)' : 'white',
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: frequency === 'monthly' ? '#059669' : '#d1d5db' }}
                >
                  {frequency === 'monthly' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />}
                </div>
                <span className="text-xs font-semibold text-gray-800">Mensual</span>
              </div>
              <div className="text-xl font-black text-gray-900">
                {isOpositor ? OPOSITOR_TIERS[opositorTier].monthly.price : PRO_CONFIG.monthly.price}
              </div>
              <div className="text-[10px] text-gray-500">
                {isOpositor ? OPOSITOR_TIERS[opositorTier].monthly.period : PRO_CONFIG.monthly.period}
              </div>
            </button>

            <button
              onClick={() => setFrequency('yearly')}
              className="relative p-3 rounded-xl border-2 text-left transition-all"
              style={{
                borderColor: frequency === 'yearly' ? '#059669' : '#e5e7eb',
                background: frequency === 'yearly' ? 'rgba(5,150,105,0.06)' : 'white',
              }}
            >
              <div
                className="absolute -top-2 right-2 px-2 py-0.2 rounded-full text-[9px] font-bold text-white bg-amber-600"
              >
                {isOpositor ? OPOSITOR_TIERS[opositorTier].yearly.savings : PRO_CONFIG.yearly.savings}
              </div>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
                  style={{ borderColor: frequency === 'yearly' ? '#059669' : '#d1d5db' }}
                >
                  {frequency === 'yearly' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />}
                </div>
                <span className="text-xs font-semibold text-gray-800">Anual</span>
              </div>
              <div className="text-xl font-black text-gray-900">
                {isOpositor ? OPOSITOR_TIERS[opositorTier].yearly.price : PRO_CONFIG.yearly.price}
              </div>
              <div className="text-[10px] text-gray-500">
                {isOpositor ? OPOSITOR_TIERS[opositorTier].yearly.period : PRO_CONFIG.yearly.period}
              </div>
            </button>
          </div>

          {/* Features list */}
          <div className="space-y-2 mb-5">
            <p className="text-xs font-bold text-gray-700 mb-1">Incluido en este plan:</p>
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-2 text-xs text-gray-600">
                <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* Botón de acción */}
          <button
            onClick={handleSubscribe}
            disabled={loadingPlan !== null}
            className="w-full py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 transition-all flex items-center justify-center gap-2 shadow-sm"
          >
            {loadingPlan ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Iniciando pago seguro...
              </>
            ) : (
              <>
                {buttonText}
                <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </button>

          {/* Link a ver todos los planes */}
          <div className="text-center mt-3">
            <button
              onClick={handleViewAllPlans}
              className="text-xs text-gray-500 hover:text-emerald-700 underline transition-colors"
            >
              Ver comparativa completa de planes
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
